/**
 * CỘT CÂN ĐỐI cho bảng tự co theo nội dung (QA 25/10/2026).
 *
 * "Không được mở rộng cột quá lớn, text tính vừa đủ, có thể xuống dòng; cột nhiều
 * chữ thì cho xuống dòng, cột ít chữ mà bị ép xuống dòng thì mở rộng ra cho cân đối."
 *
 * CÙNG thuật toán với `canDoiBang.ts` của frontend (src/utilities) / webchat (src/utils) và `CotCanDoi`
 * của app desktop (`vicchat/lib/shared/widgets/cot_can_doi.dart`) — sửa một bên
 * thì sửa cả ba.
 *
 * Giải bằng "rót nước" (water-fill) với một TRẦN chung `c`:
 *   rộng[i] = kẹp(c, tốiThiểu[i], tựNhiên[i])
 * chọn `c` sao cho tổng đúng bằng bề ngang khung. Cột ÍT chữ được đủ một dòng, các
 * cột NHIỀU chữ chia đều phần còn lại và xuống dòng; không cột nào hẹp hơn từ dài
 * nhất của nó. `table-layout: auto` của trình duyệt chia theo tỉ lệ bề rộng tự
 * nhiên nên cột dài ("Xe") nuốt gần hết chỗ, cột ngắn ("Hàng") bị bóp từng chữ.
 */

export interface TuyChonChia {
  /** Bảng hẹp hơn khung thì nới theo tỉ lệ cho kín bề ngang. */
  lapDay?: boolean;
  /** Khung cuộn ngang được: không đủ chỗ cho từ dài nhất thì giữ tối thiểu, để cuộn. */
  coCuonNgang?: boolean;
}

/** Lõi thuật toán (thuần, có test) — xem chú thích đầu tệp. */
export function chiaCotCanDoi(toiThieu: number[], tuNhien: number[], khung: number, tc: TuyChonChia = {}): number[] {
  const n = tuNhien.length;
  const lo = tuNhien.map((m, i) => Math.min(toiThieu[i], m));
  const tongTuNhien = tuNhien.reduce((a, b) => a + b, 0);
  if (!isFinite(khung) || tongTuNhien <= khung) {
    if (!tc.lapDay || !isFinite(khung) || tongTuNhien <= 0) return tuNhien.slice();
    return tuNhien.map((m) => (m * khung) / tongTuNhien);
  }
  const tongToiThieu = lo.reduce((a, b) => a + b, 0);
  if (tongToiThieu >= khung) {
    if (tc.coCuonNgang || tongToiThieu <= 0) return lo;
    return lo.map((m) => (m * khung) / tongToiThieu);
  }
  const kep = (c: number, i: number) => Math.min(Math.max(c, lo[i]), tuNhien[i]);
  const tong = (c: number) => {
    let s = 0;
    for (let i = 0; i < n; i++) s += kep(c, i);
    return s;
  };
  let a = 0;
  let b = Math.max(...tuNhien);
  for (let k = 0; k < 50; k++) {
    const c = (a + b) / 2;
    if (tong(c) < khung) a = c;
    else b = c;
  }
  // Cận DƯỚI: tổng ≤ khung, bảng không tràn khung vì sai số.
  return tuNhien.map((_, i) => kep(a, i));
}

const DAU = 'data-can-doi';
const STYLE_GOC = 'data-can-doi-style';

/** Hàng làm mốc đo: đủ số cột và không ô nào gộp cột. */
function hangMoc(table: HTMLTableElement): HTMLTableRowElement | null {
  let soCot = 0;
  for (const r of Array.from(table.rows)) {
    let s = 0;
    for (const c of Array.from(r.cells)) s += c.colSpan || 1;
    soCot = Math.max(soCot, s);
  }
  if (soCot < 2) return null;
  for (const r of Array.from(table.rows)) {
    if (r.cells.length === soCot && Array.from(r.cells).every((c) => (c.colSpan || 1) === 1)) return r;
  }
  return null;
}

function khungCua(table: HTMLTableElement): { rong: number; cuon: boolean } | null {
  const cha = table.parentElement;
  if (!cha) return null;
  const st = getComputedStyle(cha);
  const rong = cha.clientWidth - (parseFloat(st.paddingLeft) || 0) - (parseFloat(st.paddingRight) || 0);
  if (!(rong > 0)) return null;
  // Vùng cuộn ngang có thể nằm ở lớp ngoài (bong bóng markdown: `overflow: auto` ở
  // `.text-message-content`, bảng nằm trong `-inner`) -> dò lên vài lớp.
  let cuon = false;
  let el: Element | null = cha;
  for (let k = 0; el && k < 3; k++, el = el.parentElement) {
    const ox = getComputedStyle(el).overflowX;
    if (ox === 'auto' || ox === 'scroll') {
      cuon = true;
      break;
    }
  }
  return { rong, cuon };
}

/** Cân cột MỘT bảng. Bảng có ô gộp cột mà không có hàng làm mốc thì để nguyên. */
export function canDoiBang(table: HTMLTableElement): void {
  // Trả bảng về trạng thái gốc trước khi đo.
  table.querySelectorAll(`:scope > colgroup[${DAU}]`).forEach((c) => c.remove());
  if (table.hasAttribute(STYLE_GOC)) {
    const goc = table.getAttribute(STYLE_GOC) || '';
    if (goc) table.setAttribute('style', goc);
    else table.removeAttribute('style');
  } else {
    table.setAttribute(STYLE_GOC, table.getAttribute('style') || '');
  }

  // Bảng đã tự đặt `table-layout: fixed` bằng CSS (bề rộng cột cố ý) -> không đụng.
  if (!table.hasAttribute(DAU) && getComputedStyle(table).tableLayout === 'fixed') return;
  table.setAttribute(DAU, '');

  const moc = hangMoc(table);
  const khung = khungCua(table);
  if (!moc || !khung) return;

  const rongGoc = table.getBoundingClientRect().width;
  const s = table.style;
  s.tableLayout = 'auto';
  s.maxWidth = 'none';
  s.minWidth = '0';
  const doCot = () => Array.from(moc.cells).map((c) => c.getBoundingClientRect().width);
  s.width = 'max-content';
  const tuNhien = doCot().map((w) => Math.ceil(w) + 1);
  const phuTroi = table.getBoundingClientRect().width - tuNhien.reduce((a, b) => a + b, 0) + tuNhien.length;
  s.width = 'min-content';
  const toiThieu = doCot().map((w) => Math.ceil(w));

  // Bảng gốc đã kín khung (vd `width: 100%`) thì giữ kín khung.
  const lapDay = rongGoc >= khung.rong - 1;
  const rong = chiaCotCanDoi(toiThieu, tuNhien, khung.rong - Math.max(0, phuTroi), {
    lapDay,
    coCuonNgang: khung.cuon,
  });

  const cg = document.createElement('colgroup');
  cg.setAttribute(DAU, '');
  for (const w of rong) {
    const col = document.createElement('col');
    col.style.width = `${Math.floor(w)}px`;
    cg.appendChild(col);
  }
  table.insertBefore(cg, table.firstChild);
  s.tableLayout = 'fixed';
  s.width = `${Math.floor(rong.reduce((a, b) => a + b, 0) + Math.max(0, phuTroi))}px`;
}

/**
 * Tự cân mọi bảng khớp [selector] trong [goc] (document hoặc shadow root): cân ngay,
 * cân lại khi nội dung bảng đổi (MutationObserver) hoặc khung đổi bề ngang
 * (ResizeObserver). Trả về hàm huỷ theo dõi.
 */
export function theoDoiCanDoiBang(goc: Document | ShadowRoot | Element, selector = 'table'): () => void {
  const cho = new Set<HTMLTableElement>();
  const khungDaTheo = new WeakSet<Element>();
  let hen = 0;
  let mo: MutationObserver | null = null;

  const ro =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver((ds) => {
          for (const d of ds) {
            d.target.querySelectorAll(':scope > table').forEach((t) => {
              if (t.matches(selector)) cho.add(t as HTMLTableElement);
            });
          }
          henChay();
        })
      : null;

  const chay = () => {
    hen = 0;
    const ds = Array.from(cho).filter((t) => t.isConnected);
    cho.clear();
    for (const t of ds) {
      try {
        canDoiBang(t);
      } catch {
        // Lưới an toàn: cân hỏng thì bảng giữ kiểu trình duyệt tự dựng.
      }
      const cha = t.parentElement;
      if (ro && cha && !khungDaTheo.has(cha)) {
        khungDaTheo.add(cha);
        ro.observe(cha);
      }
    }
    // Bỏ các thay đổi DO CHÍNH MÌNH gây ra (colgroup, style) — không thì lặp vô hạn.
    mo?.takeRecords();
  };
  function henChay() {
    if (!hen) hen = requestAnimationFrame(chay);
  }
  const them = (t: Element | null) => {
    if (t && t.matches(selector)) cho.add(t as HTMLTableElement);
  };
  const phanTu = (n: Node | null): Element | null =>
    !n ? null : n.nodeType === 1 ? (n as Element) : n.parentElement;
  // Thay đổi BÊN TRONG một bảng -> chỉ bảng đó (và bảng ngoài nếu lồng).
  const gomTrong = (n: Node | null) => {
    let t = phanTu(n)?.closest('table') || null;
    while (t) {
      them(t);
      t = t.parentElement?.closest('table') || null;
    }
  };
  // Nút MỚI thêm -> chính nó + mọi bảng bên trong nó. KHÔNG quét cả nút cha
  // (thêm một tin vào danh sách không được kéo theo cân lại mọi bảng cũ).
  const gomMoi = (n: Node) => {
    const el = phanTu(n);
    if (!el || n.nodeType !== 1) return gomTrong(n);
    gomTrong(el);
    el.querySelectorAll('table').forEach(them);
  };

  goc.querySelectorAll('table').forEach(them);
  henChay();

  mo = new MutationObserver((rs) => {
    for (const r of rs) {
      gomTrong(r.target);
      r.addedNodes.forEach(gomMoi);
    }
    if (cho.size) henChay();
  });
  mo.observe(goc, { childList: true, subtree: true, characterData: true });

  return () => {
    mo?.disconnect();
    ro?.disconnect();
    if (hen) cancelAnimationFrame(hen);
  };
}
